
import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { WebhookConfig } from '@/types';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { z } from "zod";

const WebhookSettings: React.FC = () => {
  const [webhookUrl, setWebhookUrl] = useState<string>('');
  const [webhooksEnabled, setWebhooksEnabled] = useState<boolean>(false);
  const [isUrlValid, setIsUrlValid] = useState<boolean>(true);
  const [isTestingWebhook, setIsTestingWebhook] = useState<boolean>(false);
  const [selectedEvents, setSelectedEvents] = useState<string[]>(['new_appointment']);
  const [webhookSecret, setWebhookSecret] = useState<string>('');
  const [isSecretVisible, setIsSecretVisible] = useState<boolean>(false);
  const [signatureVerificationEnabled, setSignatureVerificationEnabled] = useState<boolean>(false);
  
  const { toast } = useToast();
  
  // Generate a random secret key on component mount
  useEffect(() => {
    if (!webhookSecret) {
      const generatedSecret = generateRandomSecret(32);
      setWebhookSecret(generatedSecret);
    }
  }, [webhookSecret]);
  
  // Sample patient data for example payload
  const sampleAppointment = {
    id: "12345",
    patient: {
      name: "Jane Smith",
      email: "jane@example.com"
    },
    doctor: {
      id: 1,
      name: "Dr. Michael Wilson"
    },
    date: "2025-05-15",
    timeSlot: "10:00 AM",
    status: "confirmed",
    timestamp: new Date().toISOString()
  };
  
  const eventOptions = [
    { id: 'new_appointment', label: 'New Appointment Created' },
    { id: 'appointment_updated', label: 'Appointment Updated' },
    { id: 'appointment_cancelled', label: 'Appointment Cancelled' },
    { id: 'appointment_completed', label: 'Appointment Marked as Completed' }
  ];

  // Function to generate a random secret key
  const generateRandomSecret = (length: number): string => {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+';
    let result = '';
    const charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
  };

  // Function to regenerate webhook secret
  const regenerateSecret = () => {
    const newSecret = generateRandomSecret(32);
    setWebhookSecret(newSecret);
    toast({
      title: "Secret Regenerated",
      description: "A new webhook secret has been generated."
    });
  };
  
  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setWebhookUrl(value);
    
    // Simple URL validation
    try {
      if (value) {
        new URL(value);
        setIsUrlValid(true);
      } else {
        setIsUrlValid(true); // Empty is valid too
      }
    } catch (err) {
      setIsUrlValid(false);
    }
  };
  
  const handleSaveWebhook = () => {
    if (!isUrlValid) {
      toast({
        title: "Invalid URL",
        description: "Please enter a valid webhook URL.",
        variant: "destructive"
      });
      return;
    }
    
    if (webhooksEnabled && !webhookUrl) {
      toast({
        title: "URL Required",
        description: "Please enter a webhook URL to enable webhooks.",
        variant: "destructive"
      });
      return;
    }
    
    const webhookConfig: WebhookConfig = {
      url: webhookUrl,
      enabled: webhooksEnabled,
      events: selectedEvents,
      secret: signatureVerificationEnabled ? webhookSecret : undefined,
      signatureVerification: signatureVerificationEnabled
    };
    
    // In a real app, this would be saved to the database
    console.log("Saving webhook configuration:", webhookConfig);
    
    toast({
      title: webhooksEnabled ? "Webhooks Enabled" : "Webhooks Disabled",
      description: webhooksEnabled 
        ? `Webhook configured for ${selectedEvents.length} event types${signatureVerificationEnabled ? ' with signature verification' : ''}.` 
        : "Webhook settings have been saved."
    });
  };
  
  const handleTestWebhook = async () => {
    if (!webhookUrl || !isUrlValid) {
      toast({
        title: "Invalid URL",
        description: "Please enter a valid webhook URL to test.",
        variant: "destructive"
      });
      return;
    }
    
    setIsTestingWebhook(true);
    
    try {
      // Create payload for test webhook
      const payload = {
        event: "test_event",
        appointment: sampleAppointment,
        timestamp: new Date().toISOString()
      };
      
      // In a real app, this would actually send a test webhook
      // with the appropriate signature if enabled
      console.log("Testing webhook:", webhookUrl);
      console.log("Payload:", payload);
      
      if (signatureVerificationEnabled) {
        console.log("Would include signature header with secret:", webhookSecret);
      }
      
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      toast({
        title: "Test Succeeded",
        description: "The test webhook was sent successfully."
      });
    } catch (error) {
      toast({
        title: "Test Failed",
        description: "There was an error sending the test webhook.",
        variant: "destructive"
      });
    } finally {
      setIsTestingWebhook(false);
    }
  };
  
  const handleEventToggle = (eventId: string) => {
    setSelectedEvents(current => 
      current.includes(eventId)
        ? current.filter(id => id !== eventId)
        : [...current, eventId]
    );
  };
  
  return (
    <div className="container mx-auto space-y-6">
      <h1 className="text-2xl font-bold">Webhook Settings</h1>
      
      <Card>
        <CardHeader>
          <CardTitle>Appointment Webhook Configuration</CardTitle>
          <CardDescription>
            Configure webhook endpoints to be notified when appointments are created or status changes.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="enable-webhooks" 
              checked={webhooksEnabled}
              onCheckedChange={(checked) => setWebhooksEnabled(!!checked)} 
            />
            <label 
              htmlFor="enable-webhooks" 
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Enable webhooks
            </label>
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">Webhook URL</label>
            <Input 
              placeholder="https://your-service.com/webhooks/quickdoc"
              value={webhookUrl}
              onChange={handleUrlChange}
              disabled={!webhooksEnabled}
              className={!isUrlValid ? "border-red-500" : ""}
            />
            {!isUrlValid && (
              <p className="text-sm text-red-500">Please enter a valid URL</p>
            )}
          </div>
          
          <div className="space-y-3">
            <label className="text-sm font-medium">Webhook Events</label>
            <div className="space-y-2">
              {eventOptions.map(event => (
                <div key={event.id} className="flex items-center space-x-2">
                  <Checkbox 
                    id={`event-${event.id}`}
                    checked={selectedEvents.includes(event.id)}
                    disabled={!webhooksEnabled}
                    onCheckedChange={() => handleEventToggle(event.id)} 
                  />
                  <label 
                    htmlFor={`event-${event.id}`}
                    className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {event.label}
                  </label>
                </div>
              ))}
            </div>
          </div>
          
          <div className="bg-slate-50 p-4 rounded-md border border-slate-100">
            <h3 className="text-sm font-medium mb-2">Webhook Payload Example</h3>
            <pre className="text-xs bg-slate-100 p-3 rounded overflow-x-auto">
{`{
  "event": "new_appointment",
  "appointment": {
    "id": "${sampleAppointment.id}",
    "patient": {
      "name": "${sampleAppointment.patient.name}",
      "email": "${sampleAppointment.patient.email}"
    },
    "doctor": {
      "id": ${sampleAppointment.doctor.id},
      "name": "${sampleAppointment.doctor.name}"
    },
    "date": "${sampleAppointment.date}",
    "timeSlot": "${sampleAppointment.timeSlot}",
    "status": "${sampleAppointment.status}",
    "timestamp": "${sampleAppointment.timestamp}"
  }
}`}
            </pre>
          </div>
        </CardContent>
        <CardFooter className="flex justify-between">
          <Button 
            variant="outline" 
            onClick={handleTestWebhook} 
            disabled={!webhookUrl || !isUrlValid || isTestingWebhook || !webhooksEnabled}
          >
            {isTestingWebhook ? "Testing..." : "Test Webhook"}
          </Button>
          <Button onClick={handleSaveWebhook}>Save Settings</Button>
        </CardFooter>
      </Card>
      
      <Card>
        <CardHeader>
          <CardTitle>Webhook Security</CardTitle>
          <CardDescription>
            Secure your webhook endpoints to ensure data integrity.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="enable-signature" 
              checked={signatureVerificationEnabled}
              disabled={!webhooksEnabled}
              onCheckedChange={(checked) => setSignatureVerificationEnabled(!!checked)} 
            />
            <label 
              htmlFor="enable-signature" 
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Enable signature verification
            </label>
          </div>
          
          <p className="text-sm text-muted-foreground">
            When signature verification is enabled, all webhook requests will include a signature header that allows you to verify the request originated from QuickDoc.
          </p>
          
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Webhook Secret</label>
              <Button 
                variant="outline" 
                size="sm"
                onClick={regenerateSecret}
                disabled={!webhooksEnabled || !signatureVerificationEnabled}
              >
                Regenerate
              </Button>
            </div>
            <div className="flex">
              <Input 
                type={isSecretVisible ? "text" : "password"}
                value={webhookSecret}
                readOnly
                disabled={!webhooksEnabled || !signatureVerificationEnabled}
                className="font-mono"
              />
              <Button 
                variant="outline" 
                type="button"
                onClick={() => setIsSecretVisible(!isSecretVisible)}
                disabled={!webhooksEnabled || !signatureVerificationEnabled}
                className="ml-2 whitespace-nowrap"
              >
                {isSecretVisible ? "Hide" : "Show"}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Keep this secret secure. You'll need it to verify webhook signatures.
            </p>
          </div>
          
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" className="w-full">
                View Signature Verification Guide
              </Button>
            </SheetTrigger>
            <SheetContent className="overflow-y-auto">
              <SheetHeader>
                <SheetTitle>Signature Verification Guide</SheetTitle>
                <SheetDescription>
                  How to verify webhook signatures in your application
                </SheetDescription>
              </SheetHeader>
              <div className="space-y-6 pt-6">
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold">How it works</h3>
                  <p className="text-sm">
                    When signature verification is enabled, each webhook request includes a <code>X-QuickDoc-Signature</code> header. This header contains a HMAC-SHA256 signature of the request body using your webhook secret.
                  </p>
                </div>
                
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold">Node.js Example</h3>
                  <pre className="text-xs bg-slate-100 p-3 rounded overflow-x-auto">
{`// Using Express
const express = require('express');
const crypto = require('crypto');
const app = express();

// Your webhook secret from QuickDoc
const webhookSecret = '${webhookSecret}';

app.post('/webhooks/quickdoc', express.raw({type: 'application/json'}), (req, res) => {
  const signature = req.headers['x-quickdoc-signature'];
  
  // Create a signature from the request body
  const hmac = crypto.createHmac('sha256', webhookSecret);
  const digest = hmac.update(req.body).digest('hex');
  
  // Compare signatures
  if (signature === digest) {
    // Signature is valid
    const payload = JSON.parse(req.body);
    console.log('Verified webhook:', payload);
    res.status(200).send('Webhook received');
  } else {
    // Signature is invalid
    console.log('Invalid webhook signature');
    res.status(401).send('Invalid signature');
  }
});

app.listen(3000, () => {
  console.log('Server listening on port 3000');
});`}
                  </pre>
                </div>
                
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold">Python Example</h3>
                  <pre className="text-xs bg-slate-100 p-3 rounded overflow-x-auto">
{`# Using Flask
from flask import Flask, request, jsonify
import hmac
import hashlib

app = Flask(__name__)

# Your webhook secret from QuickDoc
webhook_secret = '${webhookSecret}'

@app.route('/webhooks/quickdoc', methods=['POST'])
def handle_webhook():
    signature = request.headers.get('X-QuickDoc-Signature')
    payload = request.get_data()
    
    # Create a signature from the request body
    digest = hmac.new(
        webhook_secret.encode('utf-8'),
        payload,
        hashlib.sha256
    ).hexdigest()
    
    # Compare signatures
    if hmac.compare_digest(signature, digest):
        # Signature is valid
        data = request.json
        print(f"Verified webhook: {data}")
        return jsonify(success=True), 200
    else:
        # Signature is invalid
        print("Invalid webhook signature")
        return jsonify(success=False, error="Invalid signature"), 401

if __name__ == '__main__':
    app.run(port=3000)`}
                  </pre>
                </div>
                
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold">PHP Example</h3>
                  <pre className="text-xs bg-slate-100 p-3 rounded overflow-x-auto">
{`<?php
// Your webhook secret from QuickDoc
$webhookSecret = '${webhookSecret}';

// Get request headers
$headers = getallheaders();
$signature = isset($headers['X-QuickDoc-Signature']) ? $headers['X-QuickDoc-Signature'] : '';

// Get raw request body
$payload = file_get_contents('php://input');

// Create a signature from the request body
$digest = hash_hmac('sha256', $payload, $webhookSecret);

// Compare signatures
if (hash_equals($signature, $digest)) {
    // Signature is valid
    $data = json_decode($payload, true);
    error_log('Verified webhook: ' . print_r($data, true));
    http_response_code(200);
    echo json_encode(['success' => true]);
} else {
    // Signature is invalid
    error_log('Invalid webhook signature');
    http_response_code(401);
    echo json_encode(['success' => false, 'error' => 'Invalid signature']);
}
?>`}
                  </pre>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <CardTitle>Documentation</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm">
              Learn more about integrating with QuickDoc using webhooks. Our webhooks allow you to receive real-time updates whenever appointments are created, modified, or cancelled.
            </p>
            <Button variant="outline" className="w-full sm:w-auto">
              View Webhook Documentation
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default WebhookSettings;
